import { useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ORDER_COLUMNS,
  DEFAULT_SORT,
  sortOrders,
  columnType,
  type SortDir,
  type WaitlistOrder,
} from "@/utils/orderSpySort";
import { EMPTY_CELL, fmtTime, statusBadgeClass } from "@/utils/orderSpyFormat";

/**
 * The sortable waitlist orders table, shared by the Live tab and the History
 * cell dialog so a saved capture reads exactly like a live one. Owns its own
 * sort state; the input array is never mutated (sortOrders copies).
 */
export function OrdersTable({ orders }: { orders: readonly WaitlistOrder[] }) {
  const [sortKey, setSortKey] = useState<string>(DEFAULT_SORT.key);
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_SORT.dir);

  const sortedOrders = useMemo(
    () => sortOrders(orders, sortKey, columnType(sortKey), sortDir),
    [orders, sortKey, sortDir],
  );

  const onSortClick = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {ORDER_COLUMNS.map((col) => {
              const active = sortKey === col.key;
              const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
              return (
                <TableHead
                  key={col.key}
                  aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                >
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1 hover:text-foreground ${
                      active ? "text-foreground font-semibold" : ""
                    }`}
                    onClick={() => onSortClick(col.key)}
                  >
                    {col.label}
                    <Icon className="w-3.5 h-3.5 flex-none" />
                  </button>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedOrders.map((o, i) => (
            <TableRow key={`${o.OrderNo ?? "n"}-${i}`}>
              <TableCell className="tabular-nums font-medium">{o.OrderNo ?? EMPTY_CELL}</TableCell>
              <TableCell>{(o.CustomerName || "").trim() || EMPTY_CELL}</TableCell>
              <TableCell>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(
                    o.OrderStatus,
                  )}`}
                >
                  {(o.OrderStatus || "").trim() || EMPTY_CELL}
                </span>
              </TableCell>
              <TableCell className="tabular-nums">{fmtTime(o.PromiseTime)}</TableCell>
              <TableCell className="tabular-nums">{fmtTime(o.CookEndTime)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default OrdersTable;
